import React, { useEffect, useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

interface OfferItem {
    _id: string;
    offerType: 'marketplace' | 'direct';
    industry?: string;
    subIndustry?: string;
    status?: string;
    sellerId?: { _id: string; firstName: string; lastName: string; email: string } | null;
    buyerId?: { _id: string; firstName: string; lastName: string; email: string } | null;
    sellerPrice?: number;
    buyerPrice?: number;
    leadsGenerated?: number;
    leadCount?: number;
    weeklyQuantity?: string;
    offerNumber?: string;
    createdAt: string;
}

export default function BackOfficeOffers() {
    const [offers, setOffers] = useState<OfferItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const navigate = useNavigate();

    useEffect(() => {
        fetchOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, statusFilter, typeFilter]);

    const fetchOffers = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const params: any = { page, limit: 50 };
            if (statusFilter !== 'all') params.status = statusFilter;
            if (typeFilter !== 'all') params.type = typeFilter;

            const response = await axios.get(`${CONFIG.apiUrl}/admin/offers`, {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });

            setOffers(response.data.offers);
            setTotal(response.data.total);
            setTotalPages(response.data.pages);
        } catch (error) {
            console.error('Error fetching offers:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status?: string) => {
        switch (status?.toLowerCase()) {
            case 'in-progress':
            case 'running':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            case 'new':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'closed':
            case 'done':
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
            default:
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
        }
    };

    return (
        <div className="pt-[50px]">
            <Card extra="p-5">
                <div className="mb-5 flex items-center justify-between gap-2">
                    <div>
                        <div className="text-2xl font-bold text-navy-700 dark:text-white">
                            Offers Management
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            {total.toLocaleString()} total offers
                        </div>
                    </div>
                </div>

                <div className="mb-5 flex gap-2">
                    <select
                        value={typeFilter}
                        onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
                        className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                        style={{ maxWidth: '200px' }}
                    >
                        <option value="all">All Types</option>
                        <option value="marketplace">Marketplace</option>
                        <option value="direct">Direct</option>
                    </select>

                    <select
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                        className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                        style={{ maxWidth: '200px' }}
                    >
                        <option value="all">All Status</option>
                        <option value="new">New</option>
                        <option value="in-progress">In Progress</option>
                        <option value="closed">Closed</option>
                        <option value="done">Done</option>
                    </select>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200 dark:border-navy-600">
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Offer #
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Type
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Industry
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Seller
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Buyer
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Prices
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Leads
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Status
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Date
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={9} className="py-4 text-center">Loading...</td>
                                </tr>
                            ) : offers.length === 0 ? (
                                <tr>
                                    <td colSpan={9} className="py-4 text-center">No offers found</td>
                                </tr>
                            ) : (
                                offers.map((offer) => (
                                    <tr
                                        key={`${offer.offerType}-${offer._id}`}
                                        className="cursor-pointer border-b border-gray-100 hover:bg-gray-50 dark:border-navy-700 dark:hover:bg-navy-700"
                                        onClick={() => navigate(`/back-office/offers/${offer.offerType}/${offer._id}`)}
                                    >
                                        <td className="py-3 text-sm font-mono">
                                            {offer.offerNumber || offer._id.slice(-8)}
                                        </td>
                                        <td className="py-3">
                                            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                                                offer.offerType === 'marketplace'
                                                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                                                    : 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
                                            }`}>
                                                {offer.offerType === 'marketplace' ? 'Marketplace' : 'Direct'}
                                            </span>
                                        </td>
                                        <td className="py-3 text-sm">
                                            <div>{offer.industry || '-'}</div>
                                            {offer.subIndustry && (
                                                <div className="text-xs text-gray-500">{offer.subIndustry}</div>
                                            )}
                                        </td>
                                        <td className="py-3 text-sm">
                                            {offer.sellerId ? (
                                                <div>
                                                    <div className="font-medium text-purple-600 dark:text-purple-400">
                                                        {offer.sellerId.firstName} {offer.sellerId.lastName}
                                                    </div>
                                                    <div className="text-xs text-gray-500">{offer.sellerId.email}</div>
                                                </div>
                                            ) : '-'}
                                        </td>
                                        <td className="py-3 text-sm">
                                            {offer.buyerId ? (
                                                <div>
                                                    <div className="font-medium text-blue-600 dark:text-blue-400">
                                                        {offer.buyerId.firstName} {offer.buyerId.lastName}
                                                    </div>
                                                    <div className="text-xs text-gray-500">{offer.buyerId.email}</div>
                                                </div>
                                            ) : '-'}
                                        </td>
                                        <td className="py-3 text-sm">
                                            <div>Vendeur: {offer.sellerPrice != null ? `${offer.sellerPrice.toFixed(2)}€` : '-'}</div>
                                            <div>Acheteur: {offer.buyerPrice != null ? `${offer.buyerPrice.toFixed(2)}€` : '-'}</div>
                                        </td>
                                        <td className="py-3 text-sm font-medium">
                                            {offer.leadCount ?? offer.leadsGenerated ?? 0}
                                        </td>
                                        <td className="py-3">
                                            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusColor(offer.status)}`}>
                                                {offer.status || 'N/A'}
                                            </span>
                                        </td>
                                        <td className="py-3 text-sm">
                                            {new Date(offer.createdAt).toLocaleDateString()}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="mt-5 flex items-center justify-between">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Page {page} of {totalPages}
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPage(page - 1)}
                                disabled={page === 1}
                                className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                            >
                                <MdChevronLeft /> Previous
                            </button>
                            <button
                                onClick={() => setPage(page + 1)}
                                disabled={page === totalPages}
                                className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                            >
                                Next <MdChevronRight />
                            </button>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
}
